/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Conversation data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { ClientSchema } from "@/features/clients/types/clientTypes";
import { parseISO } from "date-fns";
import { z } from "zod";

// Domain schema (camelCase)
const ConversationSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  businessId: z.string(),
  phoneNumberId: z.string(),
  externalNumber: z.string(),
  lastMessageAt: z.date().nullable(),
  clientId: z.string().nullable(),
  deletedAt: z.date().nullable(),
  createdBy: z.string().nullable(),
});

const ConversationWithClientSchema = ConversationSchema.extend({
  client: ClientSchema.nullable(),
});

const CreateConversationRequestSchema = ConversationSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  deletedAt: true,
}).partial({
  lastMessageAt: true,
  createdBy: true,
});

const UpdateConversationRequestSchema = ConversationSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  businessId: true,
  phoneNumberId: true,
  externalNumber: true,
  deletedAt: true,
  createdBy: true,
}).partial();

// DTO schema (snake_case)
const ConversationDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  business_id: z.string(),
  phone_number_id: z.string(),
  external_number: z.string(),
  last_message_at: z.string().nullable(),
  client_id: z.string().nullable(),
  deleted_at: z.string().nullable(),
  created_by: z.string().nullable(),
});

const ConversationWithClientDtoSchema = ConversationDtoSchema.extend({
  client: z
    .object({
      id: z.string(),
      created_at: z.string(),
      first_name: z.string().nullable(),
      last_name: z.string().nullable(),
      email: z.string().nullable(),
      phone_number: z.string().nullable(),
      notes: z.string().nullable(),
      user_id: z.string().nullable(),
      business_id: z.string().nullable(),
    })
    .nullable(),
});

export const CreateConversationDtoSchema = ConversationDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  deleted_at: true,
}).partial({
  last_message_at: true,
  created_by: true,
});

export const UpdateConversationDtoSchema = ConversationDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  business_id: true,
  phone_number_id: true,
  external_number: true,
  deleted_at: true,
  created_by: true,
}).partial();

// Infer DTO types
export type ConversationDto = z.infer<typeof ConversationDtoSchema>;
export type ConversationWithClientDto = z.infer<
  typeof ConversationWithClientDtoSchema
>;
export type CreateConversationDto = z.infer<typeof CreateConversationDtoSchema>;
export type UpdateConversationDto = z.infer<typeof UpdateConversationDtoSchema>;

// Infer domain types
export type Conversation = z.infer<typeof ConversationSchema>;
export type ConversationWithClient = z.infer<
  typeof ConversationWithClientSchema
>;
export type CreateConversationRequest = z.infer<
  typeof CreateConversationRequestSchema
>;
export type UpdateConversationRequest = z.infer<
  typeof UpdateConversationRequestSchema
>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromConversationDtoToConversation(
  dto: ConversationDto
): Conversation {
  const conversationDto = ConversationDtoSchema.parse(dto);

  return {
    id: conversationDto.id,
    createdAt: parseISO(conversationDto.created_at),
    modifiedAt: parseISO(conversationDto.modified_at),
    businessId: conversationDto.business_id,
    phoneNumberId: conversationDto.phone_number_id,
    externalNumber: conversationDto.external_number,
    lastMessageAt: conversationDto.last_message_at
      ? parseISO(conversationDto.last_message_at)
      : null,
    clientId: conversationDto.client_id,
    deletedAt: conversationDto.deleted_at
      ? parseISO(conversationDto.deleted_at)
      : null,
    createdBy: conversationDto.created_by,
  };
}

export function fromConversationWithClientDtoToConversationWithClient(
  dto: ConversationWithClientDto
): ConversationWithClient {
  const conversationDto = ConversationWithClientDtoSchema.parse(dto);

  return {
    id: conversationDto.id,
    createdAt: parseISO(conversationDto.created_at),
    modifiedAt: parseISO(conversationDto.modified_at),
    businessId: conversationDto.business_id,
    phoneNumberId: conversationDto.phone_number_id,
    externalNumber: conversationDto.external_number,
    lastMessageAt: conversationDto.last_message_at
      ? parseISO(conversationDto.last_message_at)
      : null,
    clientId: conversationDto.client_id,
    deletedAt: conversationDto.deleted_at
      ? parseISO(conversationDto.deleted_at)
      : null,
    createdBy: conversationDto.created_by,
    client: conversationDto.client
      ? {
          id: conversationDto.client.id,
          createdAt: parseISO(conversationDto.client.created_at),
          firstName: conversationDto.client.first_name,
          lastName: conversationDto.client.last_name,
          email: conversationDto.client.email,
          phoneNumber: conversationDto.client.phone_number,
          notes: conversationDto.client.notes,
          userId: conversationDto.client.user_id,
          businessId: conversationDto.client.business_id,
        }
      : null,
  };
}

export function fromCreateConversationRequestToCreateConversationDto(
  request: CreateConversationRequest
): CreateConversationDto {
  const createConversationRequest =
    CreateConversationRequestSchema.parse(request);

  return {
    client_id: createConversationRequest.clientId,
    business_id: createConversationRequest.businessId,
    phone_number_id: createConversationRequest.phoneNumberId,
    external_number: createConversationRequest.externalNumber,
    last_message_at: createConversationRequest.lastMessageAt
      ? createConversationRequest.lastMessageAt.toISOString()
      : undefined,
    created_by: createConversationRequest.createdBy,
  };
}

export function fromUpdateConversationRequestToUpdateConversationDto(
  request: UpdateConversationRequest
): UpdateConversationDto {
  const updateConversationRequest =
    UpdateConversationRequestSchema.parse(request);

  return {
    last_message_at: updateConversationRequest.lastMessageAt
      ? updateConversationRequest.lastMessageAt.toISOString()
      : undefined,
  };
}
